import type {
  CardRecord,
  ReviewInput,
  ReviewRecord,
  Snapshot,
  StorageBackend,
} from './storage/backend';
import { IdbBackend } from './storage/idb';
import type { CardKey, Skill } from '../types';

export type { CardRecord, ReviewRecord, Snapshot };

/**
 * The app's view of a single user's progress.
 *
 * Progress is small — one card per species × skill, plus a review log — so the
 * whole working set is hydrated into memory once at boot and every read is
 * served from there. That keeps the home screen's mastery rings and the study
 * queue instant regardless of backend, and means only *writes* cross the
 * network when the PocketBase backend is active.
 *
 * Writes are write-through: memory updates synchronously, the backend write is
 * issued in the background, and a failure surfaces through `onWriteError`
 * rather than stalling the quiz mid-answer. Callers may still await
 * `flushWrites()` when they need durability before continuing.
 */

/** How far back the review log is hydrated. Only "reviews today" reads it. */
const HYDRATE_REVIEW_DAYS = 30;

let backend: StorageBackend = new IdbBackend();
let snapshot: Snapshot | null = null;
let cardsByKey = new Map<CardKey, CardRecord>();
let localReviewSeq = 0;
let pending: Promise<unknown> = Promise.resolve();

/** Called when a background write fails; main.ts renders a banner. */
let writeErrorHandler: (err: Error) => void = () => {};

export function onWriteError(handler: (err: Error) => void): void {
  writeErrorHandler = handler;
}

/** Swap the persistence layer. Must be called before hydrate(). */
export function initStorage(next: StorageBackend): void {
  backend = next;
  snapshot = null;
  cardsByKey = new Map();
}

export function backendLabel(): string {
  return backend.label;
}

/** Load this user's progress into memory. Safe to call again to refresh. */
export async function hydrate(now: Date = new Date()): Promise<void> {
  const since = now.getTime() - HYDRATE_REVIEW_DAYS * 86_400_000;
  const snap = await backend.hydrate(since);
  snapshot = snap;
  cardsByKey = new Map(snap.cards.map((c) => [c.key, c]));
  // The review it pointed at belongs to the snapshot just replaced.
  lastLogged = null;
}

/** Resolves when every issued write has reached the backend (or failed). */
export function flushWrites(): Promise<void> {
  return pending.then(
    () => undefined,
    () => undefined,
  );
}

/**
 * Hydrate lazily so the unit tests (and any code path that reads before boot
 * finishes) see a working IndexedDB-backed store without extra ceremony.
 */
async function state(): Promise<Snapshot> {
  if (!snapshot) await hydrate();
  return snapshot!;
}

/** Issue a background write, funnelling failures to the error handler. */
function write<T>(op: () => Promise<T>): void {
  pending = pending.then(op, op).catch((err: Error) => {
    writeErrorHandler(err);
  });
}

// ---- Cards -----------------------------------------------------------------

export async function getCard(key: CardKey): Promise<CardRecord | undefined> {
  await state();
  return cardsByKey.get(key);
}

export async function putCard(rec: CardRecord): Promise<void> {
  const snap = await state();
  const existing = cardsByKey.get(rec.key);
  cardsByKey.set(rec.key, rec);
  if (existing) snap.cards[snap.cards.indexOf(existing)] = rec;
  else snap.cards.push(rec);
  write(() => backend.putCard(rec));
}

/** Drop a card entirely. Only undo does this — see StorageBackend.deleteCard. */
export async function deleteCard(key: CardKey): Promise<void> {
  const snap = await state();
  const existing = cardsByKey.get(key);
  if (!existing) return;
  cardsByKey.delete(key);
  snap.cards.splice(snap.cards.indexOf(existing), 1);
  write(() => backend.deleteCard(key));
}

export async function allCards(): Promise<CardRecord[]> {
  return (await state()).cards.slice();
}

export async function cardsForSkill(skill: Skill): Promise<CardRecord[]> {
  return (await state()).cards.filter((c) => c.skill === skill);
}

// ---- Reviews ---------------------------------------------------------------

/**
 * The review written most recently by this session, and the promise of the
 * record the backend made of it.
 *
 * One deep, because undo is one deep. Holding the promise rather than an id is
 * what lets an answer be taken back in the second before its write has landed:
 * the delete simply queues behind the insert it is undoing, and reads the id
 * out of it when it arrives.
 */
let lastLogged: { local: ReviewRecord; durable: Promise<ReviewRecord> } | null = null;

export async function logReview(rec: ReviewInput): Promise<void> {
  const snap = await state();
  // The in-memory id is provisional; the backend assigns the durable one, and
  // only export (which re-reads the full log) ever needs that.
  const local: ReviewRecord = { ...rec, id: `pending-${++localReviewSeq}` };
  snap.reviews.push(local);
  const durable = pending.then(
    () => backend.logReview(rec),
    () => backend.logReview(rec),
  );
  pending = durable.catch((err: Error) => {
    writeErrorHandler(err);
  });
  lastLogged = { local, durable };
}

/**
 * Take back the review this session logged last, if it is the one the caller
 * means. Returns whether anything was removed.
 *
 * `key` is checked rather than trusted: undo is offered on the screen of the
 * question that was just answered, but a round can be left mid-answer and a
 * stale button must not delete somebody else's review. Anything but an exact
 * match on the most recent write is refused.
 */
export async function undoLastReview(key: CardKey): Promise<boolean> {
  const snap = await state();
  const entry = lastLogged;
  if (!entry || entry.local.key !== key) return false;
  const at = snap.reviews.lastIndexOf(entry.local);
  if (at < 0) return false;
  snap.reviews.splice(at, 1);
  lastLogged = null;
  write(async () => {
    // A write that never landed leaves nothing to delete, and reporting its
    // failure a second time would say "progress didn't save" about progress
    // the learner has just asked to throw away.
    const durable = await entry.durable.catch(() => null);
    if (durable) await backend.deleteReview(durable.id);
  });
  return true;
}

/**
 * Reviews at or after `ts`. Only reaches back HYDRATE_REVIEW_DAYS — enough for
 * the "reviews today" counter, which is its only caller.
 */
export async function reviewsSince(ts: number): Promise<ReviewRecord[]> {
  return (await state()).reviews.filter((r) => r.ts >= ts);
}

// ---- Settings (simple key/value) ------------------------------------------

export async function getSetting<T>(k: string): Promise<T | undefined> {
  return (await state()).settings[k] as T | undefined;
}

export async function setSetting(k: string, v: unknown): Promise<void> {
  const snap = await state();
  snap.settings[k] = v;
  const settings = { ...snap.settings };
  write(() => backend.saveSettings(settings));
}

// ---- Backup / restore ------------------------------------------------------

/** The complete progress record, including the full review log. */
export async function exportSnapshot(): Promise<Snapshot> {
  const snap = await state();
  await flushWrites();
  return {
    cards: snap.cards.slice(),
    reviews: await backend.allReviews(),
    settings: { ...snap.settings },
  };
}

/** Replace all progress for the active backend, then re-read it into memory. */
export async function importSnapshot(snap: Snapshot): Promise<void> {
  await flushWrites();
  await backend.replaceAll(snap);
  await hydrate();
}
